/************************************************
 * src/components/RedmineProjectsExport.js
 * Admin-only component for exporting Redmine projects
 ************************************************/
import React, { useState } from 'react';
import axios from './axiosInstance';
import './styles/RedmineProjectsExport.css';
import ProtectedComponent from './ProtectedComponent';
import { AuthContext } from './context/AuthContext';

const RedmineProjectsExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { user } = React.useContext(AuthContext);

  const handleExportProjects = async () => {
    setIsExporting(true);
    setExportStatus('Fetching projects from Redmine...');
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Call the backend API to export projects
      const response = await axios.get('/redmine/export-projects', {
        responseType: 'blob', // Important for file download
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'redmine_projects_hierarchy.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMessage('✓ Projects exported successfully!');
      setExportStatus('');
    } catch (error) {
      console.error('Error exporting projects:', error);
      setErrorMessage('Failed to export projects. Please try again.');
      setExportStatus('');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <ProtectedComponent requiredFunction="redmine_projects_export">
      <div className="MainContent">
        <div className="redmine-export-content">
          <h1 className="page-title">Redmine Projects Export</h1>

          <div className="export-description">
            <p>
              Export all Redmine projects with their complete hierarchy structure to an XLSX (Excel) file.
              The export includes project ID, name, identifier, status, creation date, and parent-child relationships.
            </p>
          </div>

          <div className="export-section">
            <h2>Export Options</h2>

            <div className="export-info-box">
              <div className="info-item">
                <strong>Format:</strong> Excel (.xlsx)
              </div>
              <div className="info-item">
                <strong>Includes:</strong> All active and archived projects
              </div>
              <div className="info-item">
                <strong>Hierarchy:</strong> Parent projects in bold with visual indentation
              </div>
              <div className="info-item">
                <strong>Columns:</strong> ID, Project Name, Identifier, Status, Public, Created, Updated, Description
              </div>
            </div>

            <div className="export-actions">
              <button
                className="export-button"
                onClick={handleExportProjects}
                disabled={isExporting}
              >
                {isExporting ? (
                  <>
                    <span className="spinner"></span>
                    Exporting...
                  </>
                ) : (
                  'Export All Projects to XLSX'
                )}
              </button>
            </div>

            {exportStatus && (
              <div className="status-message info">
                {exportStatus}
              </div>
            )}

            {successMessage && (
              <div className="status-message success">
                {successMessage}
              </div>
            )}

            {errorMessage && (
              <div className="status-message error">
                {errorMessage}
              </div>
            )}
          </div>

          <div className="export-notes">
            <h3>Notes</h3>
            <ul>
              <li>Root projects are highlighted with a light blue background</li>
              <li>Parent projects with children are shown in bold</li>
              <li>Child projects are indented based on their hierarchy level</li>
              <li>The export includes all 7 root projects and their subprojects</li>
            </ul>
          </div>
        </div>
      </div>
    </ProtectedComponent>
  );
};

export default RedmineProjectsExport;
